/**
 * Party details component for DEX admin dashboards.
 * Renders inline expandable details with party info and API key management.
 * Used within expanded rows of party-table.js.
 */

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Format a date string for display.
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date string
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}

/**
 * Truncate a string with ellipsis.
 * @param {string} str - String to truncate
 * @param {number} maxLen - Maximum length
 * @returns {string} Truncated string
 */
function truncate(str, maxLen = 16) {
  if (!str || str.length <= maxLen) return str || '';
  return str.substring(0, maxLen) + '...';
}

/**
 * Get display name for a role.
 * @param {string} role - Role identifier
 * @returns {string} Human-readable role name
 */
function getRoleDisplayName(role) {
  const names = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'writer': 'Writer',
    'reader': 'Reader'
  };
  return names[role] || role;
}

/**
 * Render the party details into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {Object} state.party - Party object with party_id, role, created_at, updated_at, public_key
 * @param {Array} [state.keys=[]] - Array of API key objects
 * @param {boolean} [state.keysLoading=false] - Whether keys are loading
 * @param {string} [state.currentPartyId=null] - Current session's party ID (for self-management check)
 */
export function renderPartyDetails(container, state = {}) {
  const {
    party = {},
    keys = [],
    keysLoading = false,
    currentPartyId = null
  } = state;

  const isSelf = party.party_id && party.party_id === currentPartyId;
  const isReader = party.role === 'reader';

  // Build API keys section
  let keysHtml;
  if (keysLoading) {
    keysHtml = `
      <div class="flex items-center text-gray-500 text-sm">
        <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading keys...
      </div>
    `;
  } else if (keys.length === 0) {
    keysHtml = `
      <p class="text-gray-500 text-sm">No API keys</p>
    `;
  } else {
    // Determine if this is the last key and party is self
    const isLastKey = keys.length === 1;
    const disableRevoke = isSelf && isLastKey;

    keysHtml = `
      <ul class="space-y-2">
        ${keys.map(key => {
          const revokeDisabled = disableRevoke;
          const revokeClass = revokeDisabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-red-600 hover:underline';
          const revokeTitle = revokeDisabled
            ? 'Cannot revoke your last API key'
            : 'Revoke this key';

          return `
            <li class="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
              <div class="flex items-center gap-4">
                <span class="font-mono text-xs text-gray-700" title="${escapeHtml(key.key_id)}">
                  ${escapeHtml(truncate(key.key_id, 20))}
                </span>
                <span class="text-xs text-gray-500">
                  Created: ${escapeHtml(formatDate(key.created_at))}
                </span>
              </div>
              <button
                type="button"
                class="text-sm ${revokeClass}"
                data-action="revoke-key"
                data-party-id="${escapeHtml(party.party_id)}"
                data-key-id="${escapeHtml(key.key_id)}"
                ${revokeDisabled ? 'disabled' : ''}
                title="${revokeTitle}"
              >Revoke</button>
            </li>
          `;
        }).join('')}
      </ul>
    `;
  }

  // Build public key section (for readers only)
  let publicKeyHtml = '';
  if (isReader && party.public_key) {
    publicKeyHtml = `
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Public Key
        </label>
        <div class="flex gap-2">
          <textarea
            readonly
            class="flex-1 px-3 py-2 text-xs font-mono bg-white border border-gray-300 rounded-md resize-none"
            rows="2"
            data-public-key="${escapeHtml(party.public_key)}"
          >${escapeHtml(party.public_key)}</textarea>
          <button
            type="button"
            class="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
            data-action="copy-public-key"
            data-party-id="${escapeHtml(party.party_id)}"
            title="Copy to clipboard"
          >
            <svg class="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  container.innerHTML = `
    <div class="px-6 py-4">
      <!-- Party Metadata -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <span class="block text-xs text-gray-500 uppercase">Party ID</span>
          <span class="text-sm font-mono">${escapeHtml(party.party_id)}</span>
        </div>
        <div>
          <span class="block text-xs text-gray-500 uppercase">Role</span>
          <span class="inline-flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full role-dot-${escapeHtml(party.role)}"></span>
            <span class="text-sm role-text-${escapeHtml(party.role)}">
              ${escapeHtml(getRoleDisplayName(party.role))}
            </span>
          </span>
        </div>
        <div>
          <span class="block text-xs text-gray-500 uppercase">Created</span>
          <span class="text-sm text-gray-700">${escapeHtml(formatDate(party.created_at))}</span>
        </div>
        <div>
          <span class="block text-xs text-gray-500 uppercase">Updated</span>
          <span class="text-sm text-gray-700">${escapeHtml(formatDate(party.updated_at))}</span>
        </div>
      </div>

      <!-- Public Key (for readers) -->
      ${publicKeyHtml}

      <!-- API Keys Section -->
      <div class="border-t border-gray-200 pt-4">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-700">API Keys</h4>
          <button
            type="button"
            class="text-sm text-blue-600 hover:underline"
            data-action="create-key"
            data-party-id="${escapeHtml(party.party_id)}"
          >+ Create API Key</button>
        </div>
        ${keysHtml}
      </div>
    </div>
  `;
}

/**
 * Initialize the party details with event handlers.
 * Uses delegated event handling for action buttons.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onCreateKey] - Called when create key clicked: (partyId) => void
 * @param {Function} [callbacks.onRevokeKey] - Called when revoke key clicked: (partyId, keyId) => void
 * @param {Function} [callbacks.onCopyPublicKey] - Called when copy public key clicked: (publicKey) => void
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initPartyDetails(container, callbacks = {}) {
  const { onCreateKey, onRevokeKey, onCopyPublicKey } = callbacks;

  // Delegated click handler
  function handleClick(e) {
    const actionButton = e.target.closest('[data-action]');
    if (!actionButton) return;

    const action = actionButton.dataset.action;
    const partyId = actionButton.dataset.partyId;

    switch (action) {
      case 'create-key':
        if (partyId && onCreateKey) {
          onCreateKey(partyId);
        }
        break;

      case 'revoke-key':
        if (actionButton.disabled) return;
        const keyId = actionButton.dataset.keyId;
        if (partyId && keyId && onRevokeKey) {
          onRevokeKey(partyId, keyId);
        }
        break;

      case 'copy-public-key':
        // Find the public key from the textarea
        const textarea = container.querySelector('[data-public-key]');
        if (textarea && onCopyPublicKey) {
          onCopyPublicKey(textarea.dataset.publicKey);
        }
        break;
    }
  }

  container.addEventListener('click', handleClick);

  // Return cleanup function
  return function cleanup() {
    container.removeEventListener('click', handleClick);
  };
}
